import { type ReactNode, useEffect, useRef, useState } from 'react';
import { capitalize, noop, times } from 'lodash';
import { useCursorPagination } from '@frameio/graphql-client';
import type {
  AuthenticatedUser,
  Resource,
} from '@frameio/graphql-client/generated';
import { useGetResourceMembershipsQuery } from '@frameio/graphql-client/generated';
import { intl, plural } from '@frameio/i18n';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { Emoji } from '@frameio/shared/emoji';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import { toSupportedLocaleOrDefault } from '@frameio/shared/utils/toSupportedLocale';
import { DEFAULT_INITIAL_PAGE_SIZE, imageLoader } from '@frameio/utils';
import {
  Avatar,
  Box,
  IconButton,
  Text,
  theme,
  Tooltip,
  useHoverIntent,
} from '@frameio/vapor';
import { IconArrowUpRight, IconPending } from '@frameio/vapor-icons';
import { usePreloadAccessInfo } from '../../../hooks/usePreloadAccessInfo';
import { MemberEmail } from '../../MemberAccessForm/MemberEmail';
import { ResourceIcon } from '../../ResourceIcon';
import { useGetIsListHalfVisible } from '../hooks/useGetIsListHalfVisible';
import {
  InviteAccessMemberRoleSelect,
  InviteAccessMemberRow,
  InviteAccessMemberRowSkeleton,
} from '../InviteAccessMemberRow';
import {
  StyledDivForAdornmentBefore,
  StyledDivForAdornmentTransitions,
  StyledDivForPending,
  StyledIconChevronRight,
  StyledList,
} from '../styles';
import type { InviteAccessMemberProps } from '../types';
import TEXT from './generated/TEXT/InviteAccessResource';

TEXT.DEFINE(
  {
    /** Label for the button to show/hide a resource members list */
    collapseLabel: (resourceType: string) =>
      `Show ${resourceType.toLowerCase()} Members`,

    /** Accessibility text for emoji icon */
    emoji: 'Emoji',

    /** Text showing count of group members with pluralization */
    groupMembersCount: (count: number) =>
      plural(count, {
        one: '1 Group Member',
        other: `${count} Group Members`,
      }),

    /** Label indicating current authenticated user */
    labelAuthedUser: '(you)',

    /** Text showing member count for workspaces */
    workspaceMemberCount: (count: number = 0) => {
      return plural(count, {
        one: `1 parent Workspace Member`,
        other: `${count} parent Workspace Members`,
      });
    },

    /** Text showing member count for accounts */
    accountMemberCount: (count: number = 0) => {
      const contentAdminCount = Math.max(count - 1, 0);
      return plural(contentAdminCount, {
        one: 'Owner & 1 Content Admin',
        other: `Owner & ${contentAdminCount} Content Admins`,
      });
    },

    /** Label for pending (not yet accepted invite) user */
    pending: 'Pending User',

    /** Label showing member name and role for resource access */
    resourceUserLabel: (memberName: string, roleName: string) =>
      `${memberName} has access via this resource • ${roleName}`,

    /** Text showing search result count with mixed roles */
    searchResultCount: (count: number) =>
      `${count} Matches have access via this resource • Mixed roles`,

    /** Label for button to redirect to resource member management */
    resourceRedirectLabel: (resourceName: string) =>
      `Manage members from ${resourceName}`,
  },
  { Math }
);

type Props = {
  currentUserId?: AuthenticatedUser['id'];
  disableResourceActions?: boolean;
  grantingAccessResourceId: Resource['id'];
  memberCount: Resource['memberCount'];
  onChangeResource?: (id: string) => void;
  currentResourceId: Resource['id'];
  resourceName: Resource['name'];
  resourceType: Resource['resourceType'];
  trackingSource?: TrackingSourceType;
} & Pick<InviteAccessMemberProps, 'backgroundColor'>;

export const InviteAccessResource = ({
  backgroundColor,
  currentUserId,
  disableResourceActions,
  grantingAccessResourceId,
  memberCount,
  onChangeResource = noop,
  currentResourceId,
  resourceName,
  resourceType,
  trackingSource,
}: Props) => {
  const [isShowing, setIsShowing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const shouldFetch = useGetIsListHalfVisible(listRef.current);
  const track = useTrackUnsafe();

  const [didHoverWithIntent, setDidHoverWithIntent] = useState(false);
  const { bind } = useHoverIntent(
    () => {
      if (!didHoverWithIntent) {
        setDidHoverWithIntent(true);
      }
    },
    {
      velocity: 0.2,
    }
  );

  usePreloadAccessInfo({
    fetchData: Boolean(grantingAccessResourceId) && didHoverWithIntent,
    resourceId: grantingAccessResourceId,
  });

  const accountId = useActiveAccountId();
  const {
    data,
    fetchNext: fetchNextPage,
    loading,
    variables,
  } = useCursorPagination(
    useGetResourceMembershipsQuery({
      skip: !accountId || !currentResourceId || resourceType == null,
      variables: {
        accountId: accountId ?? '',
        page: { first: DEFAULT_INITIAL_PAGE_SIZE },
        resourceId: currentResourceId,
        // resourceType is guaranteed non-null here because skip=true when null
        grantingResource: {
          id: grantingAccessResourceId,
          resourceType: resourceType!,
        },
        locale: toSupportedLocaleOrDefault(intl().locale),
      },
    })
  );

  const resourceMembers = data?.account?.resource?.indirectMemberships?.nodes;
  let metaContent: ReactNode;

  if (typeof memberCount === 'number') {
    if (resourceType === 'ACCOUNT') {
      metaContent = TEXT.accountMemberCount(memberCount);
    } else if (resourceType === 'WORKSPACE') {
      metaContent = TEXT.workspaceMemberCount(memberCount);
    }
  }

  useEffect(() => {
    const pageInfo = data?.account?.resource?.indirectMemberships?.pageInfo;
    if (!loading && shouldFetch && pageInfo) {
      fetchNextPage({
        count: DEFAULT_INITIAL_PAGE_SIZE,
        pageInfo,
      });
    }
  }, [data, fetchNextPage, loading, shouldFetch]);

  const toggleShowing = () => {
    if (!isShowing && resourceType) {
      const accessLevel = capitalize(resourceType);
      track('inherited-access-members-viewed', {
        source: trackingSource,
        access_level: accessLevel,
      });
    }
    setIsShowing((prev) => !prev);
  };

  return (
    <InviteAccessMemberRow
      {...bind}
      adornmentAfter={
        !disableResourceActions && (
          <Tooltip
            placement="top"
            title={TEXT.resourceRedirectLabel(resourceName)}
          >
            <IconButton
              icon={<IconArrowUpRight />}
              label={TEXT.resourceRedirectLabel(resourceName) as string}
              onClick={() => onChangeResource(grantingAccessResourceId)}
              variant="ghost"
              {...bind}
            />
          </Tooltip>
        )
      }
      adornmentBefore={
        <StyledDivForAdornmentBefore>
          <StyledDivForAdornmentTransitions
            aria-hidden={isHovered || isShowing}
          >
            <ResourceIcon type={resourceType ?? ''} />
          </StyledDivForAdornmentTransitions>
          <StyledDivForAdornmentTransitions
            aria-hidden={!isHovered && !isShowing}
          >
            <StyledIconChevronRight $rotate={isShowing ? 90 : 0} size={24} />
          </StyledDivForAdornmentTransitions>
        </StyledDivForAdornmentBefore>
      }
      backgroundColor={backgroundColor}
      buttonLabel={
        resourceType ? (TEXT.collapseLabel(resourceType) as string) : undefined
      }
      collapseContent={
        <StyledList ref={listRef}>
          {resourceMembers?.map((member) => {
            const nameToDisplay = member.accountUser?.isPending
              ? TEXT.pending
              : member.accountUser?.name ?? member.accountUserGroup?.name;
            const titleSuffix =
              currentUserId === member.accountUser?.userId
                ? TEXT.labelAuthedUser
                : '';
            return (
              <Box key={member.id} as="li" paddingLeft={32}>
                <InviteAccessMemberRow
                  key={member.id}
                  adornmentAfter={
                    <InviteAccessMemberRoleSelect
                      roleName={member.role?.name ?? ''}
                    />
                  }
                  adornmentBefore={
                    // renders either a group emoji or a pending user icon
                    (member.accountUserGroup && (
                      <Avatar
                        alt={`${member.accountUserGroup.emoji ?? ''} ${
                          TEXT.emoji
                        }`}
                        background="transparent"
                      >
                        {member.accountUserGroup.emoji !== undefined &&
                          member.accountUserGroup.emoji !== null && (
                            <Emoji
                              emoji={member.accountUserGroup.emoji}
                              size={24}
                            />
                          )}
                      </Avatar>
                    )) ||
                    (member.accountUser?.isPending && (
                      <StyledDivForPending $showBackground={true}>
                        <IconPending
                          color={theme.colors.text.tertiary}
                          data-testid="invite-pending-icon"
                          size={16}
                        />
                      </StyledDivForPending>
                    ))
                  }
                  avatarBackground={
                    member.accountUser?.avatarColor ?? undefined
                  }
                  avatarSrc={
                    member.accountUser?.profileImageUrl
                      ? imageLoader({
                          src: member.accountUser.profileImageUrl,
                          width: 64,
                        })
                      : undefined
                  }
                  metaContent={
                    <>
                      {member.accountUserGroup && (
                        <Text
                          color="tertiary"
                          truncate={true}
                          variant="secondary"
                        >
                          {TEXT.groupMembersCount(
                            member.accountUserGroup.memberCount ?? 0
                          )}
                        </Text>
                      )}
                      {(member.accountUser?.email ||
                        member.accountUser?.inviteEmailAddress) && (
                        <MemberEmail
                          color="tertiary"
                          email={
                            member.accountUser.email ??
                            member.accountUser.inviteEmailAddress ??
                            ''
                          }
                          enableTooltip={true}
                          truncate={true}
                          variant="secondary"
                        />
                      )}
                    </>
                  }
                  title={`${nameToDisplay} ${titleSuffix}`.trim()}
                />
              </Box>
            );
          })}
          {times(loading ? variables?.page?.first ?? 0 : 0, (index) => (
            <Box
              key={index}
              aria-hidden="true"
              paddingLeft={32}
              paddingRight={2}
            >
              <Box paddingLeft={4}>
                <InviteAccessMemberRowSkeleton />
              </Box>
            </Box>
          ))}
        </StyledList>
      }
      metaContent={
        <Text color="tertiary" truncate={true} variant="secondary">
          {metaContent}
        </Text>
      }
      onClick={toggleShowing}
      onHover={(nextIsHovered?: boolean) =>
        setIsHovered(Boolean(nextIsHovered))
      }
      showCollapseContent={isShowing}
      title={resourceName}
    />
  );
};
